import * as React from "react";
import LegalPage from "./LegalPage";
import type { LegalSection } from "./LegalPage";
import { CONTACT_EMAIL, LEGAL_LAST_UPDATED, OPERATOR_ADDRESS, OPERATOR_NAME } from "./legalDetails";

const intro =
  "This Privacy Policy explains what personal data Stellaris Mission Control stores about you, why, who can see it, and how you can have it deleted. " +
  "In short: we store your email address so you can log in, optionally your name, and which workshop group you work in. " +
  "The members of your group can see your email address next to what you enter on the group's shared mission sheet.";

const sections: LegalSection[] = [
  {
    title: "1. Who is responsible",
    body: [
      "The controller responsible for your data under the General Data Protection Regulation (GDPR) is:",
      `${OPERATOR_NAME}, ${OPERATOR_ADDRESS}`,
      `Email: ${CONTACT_EMAIL}`,
    ],
  },
  {
    title: "2. What we store about you",
    body: [
      "Your email address. It is used to log you in and to identify you in the workshop.",
      "Your password. It is stored only in hashed form; we cannot read it.",
      "Your first and last name, if you choose to give them. Both are optional.",
      "Your role on the platform (participant or organiser), which workshop and mission you chose, and which group you work in.",
      "Which entries on your group's mission sheet you were the last to change (see section 3).",
      "We do not send you any emails through the platform.",
    ],
  },
  {
    title: "3. Mission answers",
    body: [
      "During a workshop, each group enters answers and choices for an imaginary space mission on a shared sheet, for example payload choices, risks and mitigations.",
      "These answers are about the imaginary mission, not about you. They are stored per group.",
      "For each entry on the sheet we record which member of the group last changed it. The other members of your group see your email address when they point at an entry you were the last to change. This helps the group see who wrote what while working together.",
      "Please do not enter personal information about yourself or anyone else into the mission fields.",
    ],
  },
  {
    title: "4. Why we process this data",
    body: [
      "We process your account data to provide the platform to you: to let you register and log in, and to run the workshop you take part in, including the group work on the shared mission sheet (Art. 6(1)(b) GDPR).",
      "Technical data that arises when the website is delivered to your browser, such as your IP address, is processed on the basis of our legitimate interest in operating the platform securely and reliably (Art. 6(1)(f) GDPR).",
      "We do not use your data for advertising, profiling or automated decision-making, and we do not sell it.",
      "The platform is free of charge, so we do not process any payment data.",
    ],
  },
  {
    title: "5. Who can see your data",
    body: [
      "The workshop organisers can see your email address, your name if you gave one, your role, the workshop and mission you chose and the group you are in.",
      "The members of your own group can see your email address next to the entries you were the last to change on the group's mission sheet. They cannot see your name or any other account data.",
      "Participants outside your group cannot see your account data.",
      "Mission answers are shared within your group: every member of the group can see and change them. Other groups cannot see them. The workshop organisers can access them as operators of the platform.",
    ],
  },
  {
    title: "6. Cookies and browser storage",
    body: [
      "We do not use tracking cookies, analytics or advertising tools.",
      "To keep you logged in, the platform saves your login and a few settings in your browser's local storage. This stays on your device and is removed when you log out or clear your browser data.",
    ],
  },
  {
    title: "7. Service providers",
    body: [
      "Supabase provides our database and login system. Your account data and the mission answers are stored there, in the EU (region EU West, Ireland).",
      "GitHub Pages hosts the website and Cloudflare delivers it to your browser. When you open the platform, these providers process technical data such as your IP address.",
      "The platform's font is loaded from Google Fonts. When you open the platform, your browser connects to Google's servers, so Google receives your IP address.",
      "During a launch, the launch video is played in an embedded YouTube player in privacy-enhanced mode (youtube-nocookie.com). While it plays, your browser connects to YouTube's servers (Google), which receive your IP address and technical data about your device.",
      "GitHub, Cloudflare and Google are based in the United States and may process this technical data outside the EU.",
    ],
  },
  {
    title: "8. How long we keep your data",
    body: [
      "We keep your account for as long as you use it. Accounts that have not been used for one year are deleted.",
      `You can ask us to delete your account at any time by writing to ${CONTACT_EMAIL}. We delete it within 7 working days of your request.`,
      "Mission answers may remain after your account is deleted. They then no longer show who wrote them: your email address is removed with your account.",
      "If you leave a group or are moved to another one, the members of your former group no longer see your email address next to your entries.",
    ],
  },
  {
    title: "9. Minors",
    body: [
      "There is no age limit for using the platform.",
      "If you are a minor, a parent or legal guardian must agree to your use of the platform. Parents and guardians can contact us at any time to have a minor's account deleted.",
    ],
  },
  {
    title: "10. Your rights",
    body: [
      "You have the right to know what data we store about you, to have it corrected or deleted, to have its processing restricted, to receive it in a portable format, and to object to its processing.",
      `To use any of these rights, write to ${CONTACT_EMAIL}.`,
      "You also have the right to complain to a data protection supervisory authority, for example the Bavarian State Office for Data Protection Supervision (BayLDA).",
    ],
  },
  {
    title: "11. Changes to this policy",
    body: [
      "We may update this Privacy Policy when the platform or the law changes. The current version is always available on this page, with the date of the last update at the top.",
    ],
  },
  {
    title: "12. Contact",
    body: [`${OPERATOR_NAME}, ${OPERATOR_ADDRESS}`, `Email: ${CONTACT_EMAIL}`],
  },
];

export default function PrivacyPolicy(props: { handlePageChange: (page: string) => void }) {
  return (
    <LegalPage
      title="Privacy Policy"
      updated={LEGAL_LAST_UPDATED}
      intro={intro}
      sections={sections}
      handlePageChange={props.handlePageChange}
    />
  );
}
